// Code-only projection (WS-E / CK-E mandatory #1). ADDITIVE seam: the
// statement scanner (`scanner.ts`) intentionally returns untouched source
// slices — safety regexes must instead run over THIS projection, which blanks
// comments, string-literal contents, and quoted-identifier contents with
// spaces while preserving offsets (same length, newlines kept).
//
// Deliberately its own walker rather than a scanner.ts refactor: the scanner's
// statement-boundary semantics are frozen (Plan 18) and its dialect model has
// known gaps that don't matter for SPLITTING but do for SAFETY (CK-E §1):
//   • MySQL backslash-escaped strings (`'it\'s'`) — modeled here, not there.
//   • SQLite bracket AND backtick identifiers — both masked here; the scanner
//     only enables brackets for mssql / backticks for mysql.
//   • PostgreSQL nested block comments + dollar quotes — mirrored here.
//   • PostgreSQL E'…' escape strings honor backslash regardless of
//     standard_conforming_strings (WS-E fix2 F3) — modeled here.
//   • MySQL/MariaDB executable comments `/*! … */` EXECUTE their payload, so
//     the payload is projected as CODE (WS-E fix2 F1a); and `--` only opens a
//     comment when followed by whitespace/EOL (WS-E fix2 F1b).
//   • MSSQL bracketed system procedures after EXEC (`EXEC [xp_cmdshell]`) are
//     an invocation, so the identifier CONTENT is projected, not masked
//     (WS-E fix2 F2) — every OTHER bracketed identifier stays masked.
//   • PostgreSQL dollar quotes only open on a word boundary (R3 P1a): `$` is an
//     identifier character, so `a$x$` is ONE identifier, not `a` + an opener.
//   • Oracle alternative-quote literals `q'<open>…<close>'` (R3 P1b) — same
//     rules as the execution splitter (`electron/query-engine-adapters/
//     oracleExecutionSplit.ts`), which this file deliberately does not import.
// A `;` mis-split from those gaps degrades toward `parse-ambiguous` (louder),
// never toward hiding a destructive statement.

import type { SqlEngine } from '../model';

/** Half-open `[start, end)` range of one LITERAL span in the input — a string
 *  literal, a quoted identifier, a dollar-quoted body or an Oracle
 *  alternative-quote literal, delimiters INCLUDED (`q'[x]'`'s `q` too). */
export interface LiteralSpan {
  readonly start: number;
  readonly end: number;
}

export interface CodeProjection {
  /** Same length as the input; masked chars become spaces, newlines survive. */
  readonly code: string;
  /** Scan ended inside an unterminated string / block comment / quoted
   *  identifier / dollar quote. Line comments end at EOF, so they don't count
   *  (parity with the execution splitter's ambiguity rule). */
  readonly ambiguous: boolean;
  /** Every literal span, in source order, non-overlapping. COMMENTS ARE NOT
   *  LISTED: a reader that wants the code with comments dropped but literals
   *  verbatim (`collapseOptionText`) cannot tell masked literal content from
   *  masked comment content on `code` alone — the content of both is blanked to
   *  spaces, so `$$allowed$$` collapsed to `$$ $$` and an Oracle `q'[x]'` lost
   *  its `q`. The walker below already knows where each literal begins and ends,
   *  so it records them instead of every caller re-deriving the rule. */
  readonly literals: readonly LiteralSpan[];
  /** Every mysql/mariadb EXECUTABLE comment (`/*!` …) this projection revealed,
   *  DELIMITERS INCLUDED: the opener (version digits and all) and the two-char
   *  closer, both of which `code` blanks while showing the payload between them
   *  as ordinary code.
   *
   *  A reader that slices `code`'s offsets out of the ORIGINAL text therefore
   *  gets the payload WITHOUT its version guard, which is a DIFFERENT
   *  statement: the Table Designer sliced a scanned statement that way and
   *  emitted a plain `DROP TABLE t` for a buffer that only spelled it inside
   *  `/*!99999` … — a no-op on every server the guard excludes. Recorded here,
   *  like `literals`, because the walker below is the only place that knows
   *  where each wrapper begins and ends. An unterminated one runs to the end of
   *  the text. */
  readonly execComments: readonly LiteralSpan[];
}

interface DialectFlags {
  backslashEscapes: boolean;
  hashComments: boolean;
  nestedBlockComments: boolean;
  backticks: boolean;
  brackets: boolean;
  dollarQuotes: boolean;
  /** postgres: `E'…'` / `e'…'` string literals honor backslash escapes. */
  eStrings: boolean;
  /** mysql/mariadb: executable-comment (`/*!` … , `/*M!` …) payloads EXECUTE,
   *  so they are projected as code rather than masked. */
  executableComments: boolean;
  /** mysql: `--` opens a comment only when followed by whitespace or EOL. */
  dashCommentNeedsSpace: boolean;
  /** mssql: a bracketed identifier directly after EXEC/EXECUTE (or a dotted
   *  chain of them) names an invoked procedure — project its content, not a
   *  mask, so the rules can see `xp_cmdshell` / `sp_configure`. */
  execBracketReveal: boolean;
  /** oracle: `q'<open>…<close>'` / `nq'…'` alternative-quote string literals. */
  qQuotes: boolean;
}

const FLAGS: Record<SqlEngine, DialectFlags> = {
  mssql: { backslashEscapes: false, hashComments: false, nestedBlockComments: false, backticks: false, brackets: true, dollarQuotes: false, eStrings: false, executableComments: false, dashCommentNeedsSpace: false, execBracketReveal: true, qQuotes: false },
  postgres: { backslashEscapes: false, hashComments: false, nestedBlockComments: true, backticks: false, brackets: false, dollarQuotes: true, eStrings: true, executableComments: false, dashCommentNeedsSpace: false, execBracketReveal: false, qQuotes: false },
  mysql: { backslashEscapes: true, hashComments: true, nestedBlockComments: false, backticks: true, brackets: false, dollarQuotes: false, eStrings: false, executableComments: true, dashCommentNeedsSpace: true, execBracketReveal: false, qQuotes: false },
  oracle: { backslashEscapes: false, hashComments: false, nestedBlockComments: false, backticks: false, brackets: false, dollarQuotes: false, eStrings: false, executableComments: false, dashCommentNeedsSpace: false, execBracketReveal: false, qQuotes: true },
  sqlite: { backslashEscapes: false, hashComments: false, nestedBlockComments: false, backticks: true, brackets: true, dollarQuotes: false, eStrings: false, executableComments: false, dashCommentNeedsSpace: false, execBracketReveal: false, qQuotes: false },
};

const DOLLAR_OPEN = /^\$([A-Za-z_][A-Za-z0-9_]*)?\$/;

/**
 * PostgreSQL identifier-continuation characters — `$` and digits among them.
 * A `$` preceded by one of these is PART OF THAT IDENTIFIER and can never open
 * a dollar quote: PostgreSQL's lexer takes the longest match, so `a$x$` is the
 * single identifier `a$x$`, not `a` followed by a `$x$` opener (R3 P1a — a
 * column alias `a$x$` plus a later `$x$` otherwise formed a fake dollar-quote
 * span that masked a `setval(…)` call or a DELETE CTE between them).
 */
const PG_IDENT_CONT = /[A-Za-z0-9_$\u0080-\uffff]/;

/** Oracle identifier characters — a `q`/`nq` preceded by one belongs to that
 *  identifier, so `seq'x'` is not an alternative-quote opener. */
const ORACLE_IDENT = /[A-Za-z0-9_$#]/;

/** Oracle's four mirrored q-quote delimiter pairs; any other delimiter closes
 *  with itself. Mirrors `oracleExecutionSplit.ts`'s lexer. */
const Q_QUOTE_CLOSER: Record<string, string> = { '[': ']', '{': '}', '(': ')', '<': '>' };

/**
 * Oracle alternative-quote literal (`q'<open>…<close>'`, case-insensitive, with
 * the optional national-character `n` prefix) starting at `i`, or `null`.
 * `end` is one past the closing `'`; `terminated` is false when the closer is
 * missing, in which case the literal runs to EOF and the projection is
 * ambiguous. `openQuote` is the offset of the opening `'`; it and the closing
 * `'` (at `end - 1`) stay visible, everything between them is masked.
 */
function qQuoteAt(
  text: string,
  i: number,
): { end: number; terminated: boolean; openQuote: number } | null {
  const national = text[i] === 'n' || text[i] === 'N';
  const q = national ? i + 1 : i;
  if (text[q] !== 'q' && text[q] !== 'Q') return null;
  if (text[q + 1] !== "'") return null;
  const open = text[q + 2];
  // Oracle forbids whitespace as the delimiter; end-of-text is not one either.
  if (open === undefined || /\s/.test(open)) return null;
  const close = Q_QUOTE_CLOSER[open] ?? open;
  const at = text.indexOf(`${close}'`, q + 3);
  return at < 0
    ? { end: text.length, terminated: false, openQuote: q + 1 }
    : { end: at + 2, terminated: true, openQuote: q + 1 };
}

/** Mask one character: newlines survive (line structure + offsets), everything
 *  else becomes a space. */
const mask = (c: string): string => (c === '\n' ? '\n' : ' ');

/** Identifier char for EXEC-target word detection (F2). `#` is an identifier
 *  char except on engines where it opens a line comment (mysql). */
const isIdentChar = (c: string, f: DialectFlags): boolean =>
  /[A-Za-z0-9_@]/.test(c) || (c === '#' && !f.hashComments);

/** Length of a mysql/mariadb executable-comment opener at `i` (where
 *  text[i]==='/' && text[i+1]==='*'), or 0 for a plain block comment.
 *  Covers `/*!`, `/*M!`, and version-gated `/*!NNNNN` / `/*M!NNNNN` — the
 *  version digits belong to the opener and are masked with it. */
export function execCommentOpenerLen(text: string, i: number): number {
  let j = i + 2;
  if (text[j] === '!') j += 1;
  else if (text[j] === 'M' && text[j + 1] === '!') j += 2;
  else return 0;
  while (j < text.length && text[j] >= '0' && text[j] <= '9') j += 1;
  return j - i;
}

/** True when the single-quote at `i` opens a PostgreSQL `E'…'` escape string:
 *  the preceding char is a standalone `E`/`e` (word boundary before it). */
function isEStringIntro(text: string, i: number): boolean {
  const prev = i - 1 >= 0 ? text[i - 1] : '';
  if (prev !== 'E' && prev !== 'e') return false;
  const before = i - 2 >= 0 ? text[i - 2] : '';
  return !/[A-Za-z0-9_]/.test(before);
}

export type CodeProjectionOptions = {
  mysqlNoBackslashEscapes?: boolean;
  mysqlAnsiQuotes?: boolean;
};

export function projectCodeOnly(text: string, engine: SqlEngine, options: CodeProjectionOptions = {}): CodeProjection {
  const f = FLAGS[engine];
  const backslashEscapes = f.backslashEscapes && !options.mysqlNoBackslashEscapes;
  const doubleBackslashEscapes = backslashEscapes && !options.mysqlAnsiQuotes;
  const n = text.length;
  const out: string[] = new Array<string>(n);
  type State = 'code' | 'line-comment' | 'block-comment' | 'single' | 'double' | 'backtick' | 'bracket' | 'dollar';
  let state: State = 'code';
  let blockDepth = 0;
  let dollarTag = '';
  let i = 0;
  // F1a: inside a mysql executable-comment payload (we stay in `code`, watching
  // for the `*/` closer). `execCommentStart` is the offset of its opener — see
  // `CodeProjection.execComments`.
  let execComment = false;
  let execCommentStart = -1;
  const execComments: LiteralSpan[] = [];
  // F2: EXEC-invocation target tracking. `expecting-part` = next identifier/
  // bracket is a proc-name part; `after-part` = a part just ended (a following
  // `.` continues the dotted chain).
  let execState: 'none' | 'expecting-part' | 'after-part' = 'none';
  let wordStart = -1;
  let bracketReveal = false;
  // F3: the currently-open single-quoted string honors backslash escapes.
  let singleBackslash = false;
  // R3 P1b: an Oracle q-quote ran to EOF without its `<close>'` — the scan is
  // ambiguous exactly like an unterminated ordinary string.
  let unterminatedQQuote = false;
  // Literal spans (see `CodeProjection.literals`): `spanStart` is the offset of
  // the opener of the literal state currently open.
  const literals: LiteralSpan[] = [];
  let spanStart = -1;
  const endSpan = (end: number): void => {
    if (spanStart >= 0) literals.push({ start: spanStart, end });
    spanStart = -1;
    state = 'code';
  };

  // Finalize the identifier word ending just before `i`, advancing the EXEC
  // target state machine (F2).
  const finalizeWord = (): void => {
    const word = text.slice(wordStart, i);
    wordStart = -1;
    if (/^exec(ute)?$/i.test(word)) execState = 'expecting-part';
    else if (execState === 'expecting-part') execState = 'after-part';
    else execState = 'none';
  };

  // Consume a closing delimiter run for quote states: doubled delimiter is an
  // escape (stay inside), single delimiter closes. Returns the next index.
  const closeOrEscape = (idx: number, delim: string, close: (end: number) => void): number => {
    if (text[idx + 1] === delim) {
      out[idx] = mask(text[idx]);
      out[idx + 1] = mask(text[idx + 1]);
      return idx + 2;
    }
    out[idx] = delim; // keep the closing delimiter visible
    close(idx + 1);
    return idx + 1;
  };

  while (i < n) {
    const c = text[i];
    const c2 = i + 1 < n ? text[i + 1] : '';

    if (state === 'code') {
      const identChar = isIdentChar(c, f);
      if (!identChar && wordStart >= 0) finalizeWord();

      // F1a: the closer of an executable-comment payload.
      if (execComment && c === '*' && c2 === '/') {
        out[i] = ' '; out[i + 1] = ' ';
        execComment = false;
        execComments.push({ start: execCommentStart, end: i + 2 });
        i += 2;
        continue;
      }

      if (c === '-' && c2 === '-') {
        // F1b: mysql needs whitespace/EOL after `--` for it to be a comment;
        // `--1` is double-negation arithmetic, not a comment.
        const after = i + 2 < n ? text[i + 2] : '';
        const isComment = !f.dashCommentNeedsSpace || after === '' || after === ' ' || after === '\t' || after === '\n' || after === '\r';
        if (isComment) { state = 'line-comment'; out[i] = ' '; out[i + 1] = ' '; execState = 'none'; i += 2; continue; }
        out[i] = c; execState = 'none'; i++; continue; // arithmetic `-`, stay in code
      }
      if (f.hashComments && c === '#') { state = 'line-comment'; out[i] = ' '; execState = 'none'; i++; continue; }
      if (c === '/' && c2 === '*') {
        // F1a: mysql/mariadb executable comment — mask the opener/closer only,
        // project the payload as code (strings inside it still get masked
        // because the payload runs through this same walker).
        if (f.executableComments) {
          const openLen = execCommentOpenerLen(text, i);
          if (openLen > 0) {
            for (let k = 0; k < openLen; k++) out[i + k] = ' ';
            execComment = true;
            execCommentStart = i;
            execState = 'none';
            i += openLen;
            continue;
          }
        }
        state = 'block-comment'; blockDepth = 1; out[i] = ' '; out[i + 1] = ' '; execState = 'none'; i += 2; continue;
      }
      if (c === "'") { state = 'single'; spanStart = i; singleBackslash = f.eStrings && isEStringIntro(text, i); out[i] = c; execState = 'none'; i++; continue; }
      if (c === '"') { state = 'double'; spanStart = i; out[i] = c; execState = 'none'; i++; continue; }
      if (f.backticks && c === '`') { state = 'backtick'; spanStart = i; out[i] = c; execState = 'none'; i++; continue; }
      if (f.brackets && c === '[') {
        // F2: reveal only when this bracket names an EXEC invocation target.
        bracketReveal = f.execBracketReveal && execState === 'expecting-part';
        if (!bracketReveal) execState = 'none';
        state = 'bracket'; spanStart = i; out[i] = c; i++; continue;
      }
      // R3 P1b: an Oracle alternative-quote literal. Checked BEFORE the
      // identifier branch (its `q`/`n` opener is an identifier char) and only
      // on a word boundary, so `seq'x'` stays an identifier + a plain string.
      if (f.qQuotes && (c === 'q' || c === 'Q' || c === 'n' || c === 'N')
        && !(i > 0 && ORACLE_IDENT.test(text[i - 1]))) {
        const q = qQuoteAt(text, i);
        if (q) {
          for (let k = i; k < q.end; k++) out[k] = mask(text[k]);
          out[q.openQuote] = "'";
          if (q.terminated) out[q.end - 1] = "'";
          else unterminatedQQuote = true;
          // The `q`/`nq` prefix belongs to the literal, so the span starts at
          // `i` — sliced from the original it is the whole `q'[…]'` form.
          literals.push({ start: i, end: q.end });
          execState = 'none';
          i = q.end;
          continue;
        }
      }
      // R3 P1a: `$` only opens a dollar quote on a word boundary — after an
      // identifier character it CONTINUES that identifier (`a$x$`).
      if (f.dollarQuotes && c === '$' && !(i > 0 && PG_IDENT_CONT.test(text[i - 1]))) {
        const m = text.slice(i).match(DOLLAR_OPEN);
        if (m) {
          state = 'dollar';
          spanStart = i;
          dollarTag = m[0];
          for (let k = 0; k < dollarTag.length; k++) out[i + k] = dollarTag[k];
          execState = 'none';
          i += dollarTag.length;
          continue;
        }
      }
      if (identChar) {
        if (wordStart < 0) wordStart = i;
        out[i] = c;
        i++;
        continue;
      }
      // Non-identifier, non-trigger code char: advance the EXEC chain state.
      if (c === '.') execState = execState === 'after-part' ? 'expecting-part' : 'none';
      else if (!/\s/.test(c)) execState = 'none';
      out[i] = c;
      i++;
    } else if (state === 'line-comment') {
      if (c === '\n') { state = 'code'; out[i] = c; } else { out[i] = ' '; }
      i++;
    } else if (state === 'block-comment') {
      if (f.nestedBlockComments && c === '/' && c2 === '*') {
        blockDepth++;
        out[i] = mask(c); out[i + 1] = ' ';
        i += 2;
      } else if (c === '*' && c2 === '/') {
        blockDepth--;
        out[i] = ' '; out[i + 1] = ' ';
        i += 2;
        if (blockDepth === 0) state = 'code';
      } else {
        out[i] = mask(c);
        i++;
      }
    } else if (state === 'single') {
      if ((backslashEscapes || singleBackslash) && c === '\\') { out[i] = ' '; if (i + 1 < n) out[i + 1] = mask(c2); i += 2; continue; }
      if (c === "'") { i = closeOrEscape(i, "'", endSpan); continue; }
      out[i] = mask(c);
      i++;
    } else if (state === 'double') {
      if (doubleBackslashEscapes && c === '\\') { out[i] = ' '; if (i + 1 < n) out[i + 1] = mask(c2); i += 2; continue; }
      if (c === '"') { i = closeOrEscape(i, '"', endSpan); continue; }
      out[i] = mask(c);
      i++;
    } else if (state === 'backtick') {
      if (c === '`') { i = closeOrEscape(i, '`', endSpan); continue; }
      out[i] = mask(c);
      i++;
    } else if (state === 'bracket') {
      if (c === ']') {
        if (c2 === ']') {
          // Escaped `]]` — an in-name literal `]`. Stay inside. In reveal mode
          // keep the chars literal (a `]` never appears in a dangerous proc
          // name, so this can't fabricate a match); otherwise mask.
          if (bracketReveal) { out[i] = ']'; out[i + 1] = ']'; } else { out[i] = ' '; out[i + 1] = ' '; }
          i += 2;
          continue;
        }
        out[i] = ']';
        endSpan(i + 1);
        i++;
        if (bracketReveal) { execState = 'after-part'; bracketReveal = false; }
        continue;
      }
      out[i] = bracketReveal ? c : mask(c);
      i++;
    } else {
      // dollar
      if (text.startsWith(dollarTag, i)) {
        for (let k = 0; k < dollarTag.length; k++) out[i + k] = dollarTag[k];
        i += dollarTag.length;
        endSpan(i);
        continue;
      }
      out[i] = mask(c);
      i++;
    }
  }

  // An unterminated literal runs to EOF — record it so a reader slicing spans
  // still gets its text verbatim (the scan is reported `ambiguous` as well).
  if (spanStart >= 0 && state !== 'line-comment' && state !== 'block-comment') literals.push({ start: spanStart, end: n });
  // An executable comment whose closer never came covers the rest of the text:
  // its payload was projected as code, so a reader keeps the guard by widening
  // to the end rather than emitting a payload the server never runs on its own.
  if (execComment) execComments.push({ start: execCommentStart, end: n });
  const ambiguous = unterminatedQQuote || (state !== 'code' && state !== 'line-comment');
  return { code: out.join(''), ambiguous, literals, execComments };
}
